import { useAppSelector } from 'hooks/redux';
import type { SyntheticEvent } from 'react';
import React, { createRef, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { tracker } from 'app-components/utils/core';

import UserGuideIcon from '@mui/icons-material/ArticleOutlined';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

import HelpIcon from 'assets/icons/nav-icons/help.inline.svg';
import NotificationsNavIcon from 'containers/App/Header/Notifications/NotificationsNavIcon';
import type { FeaturePage } from 'containers/App/paths';
import { makeNavigationItemId } from 'containers/App/paths';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import RefreshButton from '../RefreshButton';
import { useNavBarStyle, useNavItemStyles } from '../commonNavStyles';

// Map paragraphs to header tag within navbar
const NAV_MAPPING = { paragraphRegular: 'h4' };

export const GroupNavItem = React.memo(function GroupNavItem() {
  const { classes: navBarClasses } = useNavBarStyle();
  return (
    <div className={navBarClasses.group}>
      <div className={navBarClasses.groupDivider} />
    </div>
  );
});

export const MainNavItem = React.memo(function MainNavItem({
  nav,
  displayNode,
  closeHeader,
  iconRef,
}: {
  nav: FeaturePage;
  displayNode?: string;
  closeHeader: () => void;
  iconRef?: React.RefObject<HTMLDivElement>;
}) {
  const { classes, cx } = useNavItemStyles();
  const location = useLocation();
  const isSelected = nav.path === `/${location.pathname.split('/')[1]}`;

  const navFeatureFlags = useAppSelector((state) => {
    let show = nav.featureFlag ? featureFlagsSelectors.getFeatureFlag(state, nav.featureFlag) : null;
    if (nav.businessEnabled) {
      show = entitlementsSelectors.getBusinessEnabled(state);
    }
    return show;
  });

  if (!nav || (nav.featureFlag && !navFeatureFlags)) {
    return null;
  }
  const IconComponent = nav.icon;

  const ref = createRef<HTMLAnchorElement>();
  const aId = makeNavigationItemId(nav);
  let { path } = nav;
  if (nav.useSelectedAccount && displayNode) {
    path = `${path}?displayNode=${displayNode}`;
  }
  return (
    <ButtonBase
      onClick={() => {
        if (nav.title === 'Spending Plan') {
          tracker.track(tracker.events.viewSpendingPlan, { initiator: 'navigation bar' });
        }
        closeHeader();
      }}
      className={isSelected ? cx(classes.navMenuItem, classes.selectedItem) : classes.navMenuItem}
      to={path}
      component={NavLink}
      id={aId}
      ref={ref}
    >
      {/* Below will keep the full nav item w/ darker background color when selected */}
      {/* IMPORTANT: this span is used in a css selector by the FutureTransactionGuidedPopover component */}
      <span ref={iconRef} className={classes.navBarIconSpan}>
        <IconComponent aria-label={nav.alt} className={classes.navIcon} />
      </span>
      <Typography
        variant='paragraphRegular'
        variantMapping={NAV_MAPPING}
        className={isSelected ? classes.selectedNavText : classes.navText}
      >
        {nav.title}
      </Typography>
    </ButtonBase>
  );
});

export const NotificationsNavItem = React.memo(function NotificationsNavItem({
  closeDrawer,
}: {
  closeDrawer: () => void;
}) {
  const { classes } = useNavItemStyles();

  const notificationsRef = useRef<HTMLButtonElement>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsClose = () => setShowNotifications(false);

  const inProductNotificationsOnClick = (event: SyntheticEvent) => {
    closeDrawer();
    setShowNotifications(!showNotifications);
    event.stopPropagation();
  };
  return (
    <ButtonBase
      id='in-product-notifications'
      ref={notificationsRef}
      className={classes.navMenuItem}
      onClick={inProductNotificationsOnClick}
    >
      <NotificationsNavIcon
        buttonRef={notificationsRef}
        open={Boolean(showNotifications)}
        onClose={notificationsClose}
      />
      <Typography variant='paragraphRegular' className={classes.navText}>
        Notifications
      </Typography>
    </ButtonBase>
  );
});

export const RefreshNavItem = React.memo(function RefreshNavItem({
  closeDrawer,
}: {
  closeDrawer: () => void;
}) {
  const { classes } = useNavItemStyles();
  return (
    <RefreshButton closeHeader={closeDrawer} id='sync-arrow' classes={{ refreshDivButton: classes.navMenuItem }} />
  );
});

export const IntercomNavItem = React.memo(function IntercomNavItem() {
  const { classes, cx } = useNavItemStyles();
  return (
    <ButtonBase id='intercom-launcher' className={cx(classes.navMenuItem, classes.navMenuItemSupport)}>
      <HelpIcon className={classes.navIcon} />
      <Typography variant='paragraphRegular' className={classes.navText}>
        Help Center
      </Typography>
    </ButtonBase>
  );
});

export const UserGuideNavItem = React.memo(function UserGuideNavItem() {
  const { classes, cx } = useNavItemStyles();

  const isUserGuideEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'userGuideEnabled'));

  if (!isUserGuideEnabled) return null;

  const userGuideOnClick = () => {
    window.dispatchEvent(new CustomEvent('user-guide:open', { detail: { guideUrl: '/' } }));
  };

  return (
    <ButtonBase
      id='user-guide-launcher'
      className={cx(classes.navMenuItem, classes.navMenuItemSupport)}
      onClick={userGuideOnClick}
    >
      {<UserGuideIcon className={classes.navIcon} />}
      <Typography variant='paragraphRegular' className={classes.navText}>
        User Guide
      </Typography>
    </ButtonBase>
  );
});
